import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import Analysis from '../views/Analysis.vue'
import AnalysesDashboard from '../views/AnalysesDashboard.vue'
import Impressum from '../views/Impressum.vue'
import Datenschutz from '../views/Datenschutz.vue'
import Articles from '../views/Articles.vue'
import OptimaleRaumaufteilung from '../views/articles/01_OptimaleRaumaufteilung.vue'
import NachhaltigerGrundriss from '../views/articles/02_NachhaltigerGrundriss.vue'
import { resolveFirebaseEmailActionRedirect } from '@/utils/firebaseEmailActionRoute.mjs'
import { useAuthStore } from '@/stores/auth'
import { wantsPricing } from '@/router/pricingIntent'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home
  },
  {
    path: '/analysis',
    name: 'Analysis',
    component: Analysis
  },
  {
    // Authenticated dashboard listing every saved analysis. Lives at
    // its own URL (separate from /analysis, which is the upload/new-
    // analysis surface) so the "Meine Analysen" link can land here
    // without conflating "start something new" with "review what I've
    // already done".
    path: '/meine-analysen',
    name: 'AnalysesDashboard',
    component: AnalysesDashboard
  },
  {
    path: '/analysis/:id',
    name: 'AnalysisHub',
    component: Analysis
  },
  {
    path: '/analysis/:id/360',
    name: 'Analysis360',
    component: Analysis
  },
  {
    path: '/analysis/:id/studio',
    name: 'AnalysisStudio',
    component: Analysis
  },
  {
    path: '/impressum',
    name: 'Impressum',
    component: Impressum
  },
  {
    path: '/datenschutz',
    name: 'Datenschutz',
    component: Datenschutz
  },
  {
    path: '/artikel',
    name: 'Articles',
    component: Articles
  },
  {
    path: '/artikel/optimal-floorplan',
    name: 'OptimaleRaumaufteilung',
    component: OptimaleRaumaufteilung
  },
  {
    path: '/artikel/nachhaltiger-grundriss',
    name: 'NachhaltigerGrundriss',
    component: NachhaltigerGrundriss
  },
  {
    path: '/sign-in',
    name: 'SignIn',
    component: () => import('@/components/auth/SignIn.vue')
  },
  {
    path: '/sign-up',
    name: 'SignUp',
    component: () => import('@/components/auth/SignUp.vue')
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/components/auth/ForgotPassword.vue')
  },
  {
    path: '/auth/action',
    name: 'AuthAction',
    component: () => import('@/components/auth/ResetPassword.vue')
  },
  {
    path: '/thank-you',
    name: 'ThankYou',
    component: () => import('@/views/ThankYou.vue')
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

// `wantsPricing` lives in ./pricingIntent so both this guard and Home.vue can
// import it without forming a router ↔ Home import cycle. Re-exported here for
// backwards-compatible imports from '@/router'.
export { wantsPricing } from '@/router/pricingIntent'

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard
router.beforeEach((to, from, next) => {
  const redirect = resolveFirebaseEmailActionRedirect({
    path: to.path,
    query: to.query,
  })

  if (redirect && to.name !== 'AuthAction') {
    next(redirect)
    return
  }

  // Signed-in users have no business on the marketing landing page —
  // they want the dashboard rail, not the hero section. Redirect Home
  // straight to /meine-analysen. Late auth restore (Firebase observer
  // firing AFTER the route already resolved) is handled by Home.vue
  // watching isAuthenticated and pushing the same redirect.
  //
  // EXCEPTION 1: the pricing section lives only on the Home landing, so an
  // explicit pricing intent (?pricing=1, set by the account-rail "Buy
  // credits" action and the post-signup pricing return) must be honoured.
  // Without this carve-out the redirect strands signed-in users with no
  // route to the purchase flow at all.
  //
  // EXCEPTION 2: only redirect when ARRIVING at Home from a different route.
  // PricingSection consumes ?pricing=1 and then `router.replace`s to a clean
  // "/" — a Home→Home navigation. Without the `from.name !== 'Home'` guard
  // that internal cleanup re-triggers this check (now without the pricing
  // query) and bounces the user straight back off the pricing page.
  if (to.name === 'Home' && from.name !== 'Home' && !wantsPricing(to)) {
    try {
      const authStore = useAuthStore()
      if (authStore.isAuthenticated) {
        next({ name: 'AnalysesDashboard' })
        return
      }
    } catch (err) {
      // Store not initialized yet during very first router resolution
      // — fall through to render Home; the in-view watcher catches up.
    }
  }

  next();
});

// Google Analytics route tracking
router.afterEach((to) => {
  // Only track if gtag is available and GA ID is configured
  if (window.gtag && process.env.VUE_APP_GA_ID) {
    window.gtag('config', process.env.VUE_APP_GA_ID, {
      'page_path': to.fullPath
    });
  }
})

export default router
